import {
  BookOpenText,
  Check,
  FileText,
  GitMerge,
  type LucideIcon,
  Sparkles,
  Telescope,
  Upload,
} from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/utils/cn'

type StageKey =
  | 'description'
  | 'documents'
  | 'schema'
  | 'entityResolution'
  | 'research'
  | 'investigations'
  | 'report'

interface Stage {
  key: StageKey
  icon: LucideIcon
  href: string
  done: boolean
  hint?: string
  description: string
}

export interface CaseJourneyStripViewProps {
  caseId: string
  schemaStatus?: { hasSchema: boolean; isError: boolean; isLoading: boolean }
  hasDescription: boolean
  documentCount: number
  /** Pending entity-resolution matches (POSSIBLE_MATCH pairs). */
  pendingMatches: number
  /** True when the entity-resolution count could not be loaded. */
  entityResolutionError: boolean
  /** Number of hypotheses with at least one run. */
  researchRuns: number
  /** Total saved investigations. */
  investigationsTotal: number
  /** Saved investigations marked for the report. */
  investigationsInReport: number
}

/**
 * Pure presentation for the case roadmap. The connected strip in
 * `features/case-overview` derives the progress signals (entity-resolution,
 * research, investigations) and passes them in as props.
 */
export function CaseJourneyStripView({
  caseId,
  schemaStatus,
  hasDescription,
  documentCount,
  pendingMatches,
  entityResolutionError,
  researchRuns,
  investigationsTotal,
  investigationsInReport,
}: CaseJourneyStripViewProps) {
  const { t } = useTranslation()

  const stages: Stage[] = useMemo(() => {
    const docs = documentCount
    return [
      {
        key: 'description',
        icon: FileText,
        href: `/cases/${caseId}/overview`,
        done: hasDescription,
        hint: hasDescription
          ? t('overview.caseJourney.stages.description.doneHint')
          : t('overview.caseJourney.stages.description.pendingHint'),
        description: t('overview.caseJourney.stages.description.description'),
      },
      {
        key: 'documents',
        icon: Upload,
        href: `/cases/${caseId}/documents`,
        done: docs > 0,
        hint:
          docs > 0
            ? t('overview.caseJourney.stages.documents.doneHint', { count: docs })
            : t('overview.caseJourney.stages.documents.pendingHint'),
        description: t('overview.caseJourney.stages.documents.description'),
      },
      {
        key: 'schema',
        icon: Sparkles,
        href: `/cases/${caseId}/schema`,
        done: schemaStatus?.hasSchema ?? false,
        hint: t(
          `overview.caseJourney.stages.schema.${schemaStatus?.isError ? 'errorHint' : schemaStatus?.isLoading ? 'loadingHint' : schemaStatus?.hasSchema ? 'doneHint' : 'pendingHint'}`
        ),
        description: t('overview.caseJourney.stages.schema.description'),
      },
      {
        key: 'entityResolution',
        icon: GitMerge,
        href: `/cases/${caseId}/entity-resolution`,
        // Treat error as "not done" so the stage doesn't falsely flip to a
        // completed checkmark when the count endpoint is unreachable.
        done: docs > 0 && !entityResolutionError && pendingMatches === 0,
        hint: entityResolutionError
          ? t('overview.caseJourney.stages.entityResolution.errorHint')
          : pendingMatches > 0
            ? t('overview.caseJourney.stages.entityResolution.pendingHint', {
                count: pendingMatches,
              })
            : docs > 0
              ? t('overview.caseJourney.stages.entityResolution.doneHint')
              : t('overview.caseJourney.stages.entityResolution.idleHint'),
        description: t('overview.caseJourney.stages.entityResolution.description'),
      },
      {
        key: 'research',
        icon: Telescope,
        href: `/cases/${caseId}/search`,
        done: researchRuns > 0,
        hint:
          researchRuns > 0
            ? t('overview.caseJourney.stages.research.doneHint', { count: researchRuns })
            : t('overview.caseJourney.stages.research.pendingHint'),
        description: t('overview.caseJourney.stages.research.description'),
      },
      {
        key: 'investigations',
        icon: BookOpenText,
        href: `/cases/${caseId}/investigations`,
        done: investigationsTotal > 0,
        hint:
          investigationsTotal > 0
            ? t('overview.caseJourney.stages.investigations.doneHint', {
                count: investigationsTotal,
              })
            : t('overview.caseJourney.stages.investigations.pendingHint'),
        description: t('overview.caseJourney.stages.investigations.description'),
      },
      {
        key: 'report',
        icon: FileText,
        href: `/cases/${caseId}/case-summary`,
        done: investigationsInReport > 0,
        hint:
          investigationsInReport > 0
            ? t('overview.caseJourney.stages.report.doneHint', { count: investigationsInReport })
            : t('overview.caseJourney.stages.report.pendingHint'),
        description: t('overview.caseJourney.stages.report.description'),
      },
    ]
  }, [
    caseId,
    schemaStatus,
    hasDescription,
    documentCount,
    pendingMatches,
    entityResolutionError,
    researchRuns,
    investigationsTotal,
    investigationsInReport,
    t,
  ])

  const doneCount = stages.filter(s => s.done).length
  // The "current" stage is the first not-done one — gets the highlighted glow.
  const currentIndex = stages.findIndex(s => !s.done)

  // Progress index drives the filled gradient on the axis. When every stage is
  // done, push the fill to the last node so the axis renders fully filled.
  const progressIndex = currentIndex < 0 ? stages.length - 1 : currentIndex
  // Inner axis spans 8% → 92% of the strip width; the filled portion stops at
  // the centre of the current stage column.
  const innerSpan = 100 - 16 // %
  const fillPercent =
    stages.length > 1 ? (innerSpan * progressIndex) / (stages.length - 1) : innerSpan

  return (
    <Card className="p-4 md:p-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
        <div className="min-w-0 md:flex-1">
          <span className="text-2xs font-medium uppercase tracking-widest text-muted-foreground/60">
            {t('overview.caseJourney.eyebrow')}
          </span>
          <h3 className="mt-1 text-base font-semibold text-foreground">
            {t('overview.caseJourney.title')}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {doneCount === stages.length
              ? t('overview.caseJourney.subtitleComplete')
              : t('overview.caseJourney.subtitle')}
          </p>
        </div>
        <span className="shrink-0 self-start rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-2xs font-medium tabular-nums text-muted-foreground">
          {t('overview.caseJourney.progress', { done: doneCount, total: stages.length })}
        </span>
      </div>

      <TooltipProvider delayDuration={150}>
        <div className="relative mt-4 md:mt-6 md:pb-2">
          {/* Phone: the stages stack into a vertical stepper, so the axis runs
              down through the numbered nodes instead of across. */}
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-6 left-[21px] top-6 w-px bg-border md:hidden"
          />
          {/* Background axis — aligned to the centre of the numbered nodes. */}
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-[28px] left-[8%] right-[8%] hidden h-[3px] rounded-full bg-border md:block"
          />
          {/* Filled axis — gradient from the leftmost node up to the current one. */}
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-[28px] left-[8%] hidden h-[3px] rounded-full bg-gradient-to-r from-primary to-primary/80 md:block"
            style={{ width: `${fillPercent}%` }}
          />

          <ol
            className="relative grid max-md:grid-cols-1!"
            style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` }}
          >
            {stages.map((stage, index) => (
              <li key={stage.key}>
                <StageColumn
                  stage={stage}
                  label={t(`overview.caseJourney.stages.${stage.key}.title`)}
                  isCurrent={index === currentIndex}
                  index={index + 1}
                />
              </li>
            ))}
          </ol>
        </div>
      </TooltipProvider>
    </Card>
  )
}

function StageColumn({
  stage,
  label,
  isCurrent,
  index,
}: {
  stage: Stage
  label: string
  isCurrent: boolean
  index: number
}) {
  const Icon = stage.icon
  const accent = stage.done || isCurrent

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={stage.href}
          aria-current={isCurrent ? 'step' : undefined}
          className={cn(
            'group/stage relative flex items-center gap-3 rounded-xl px-2 py-2 outline-none transition-colors md:flex-col md:gap-2',
            'hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-primary/50'
          )}
        >
          {/* Icon chip */}
          <span
            className={cn(
              'inline-flex size-9 items-center justify-center rounded-xl border max-md:order-last max-md:shrink-0',
              accent
                ? 'border-primary/40 bg-primary/15 text-primary'
                : 'border-border bg-muted/40 text-muted-foreground'
            )}
          >
            <Icon className="size-4" />
          </span>

          {/* Label + hint */}
          <div className="flex flex-col gap-0.5 max-md:min-w-0 max-md:flex-1 md:items-center md:text-center">
            <span
              className={cn(
                'text-sm font-semibold',
                accent ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              {label}
            </span>
            {stage.hint && (
              <span className="line-clamp-2 text-2xs text-muted-foreground md:line-clamp-1">
                {stage.hint}
              </span>
            )}
          </div>

          {/* Tick connector — visually links the column to the axis below */}
          <span
            aria-hidden
            className={cn('mt-1 h-3 w-px max-md:hidden', accent ? 'bg-primary/60' : 'bg-border')}
          />

          {/* Numbered node sitting on the axis */}
          <span
            className={cn(
              'relative z-10 inline-flex size-7 items-center justify-center rounded-full border-2 text-xs font-semibold tabular-nums transition-shadow max-md:order-first max-md:shrink-0',
              stage.done && 'border-primary bg-primary text-primary-foreground',
              !stage.done && isCurrent && 'border-primary bg-background text-primary',
              !stage.done && !isCurrent && 'border-border bg-background text-muted-foreground',
              isCurrent &&
                'shadow-[0_0_0_5px_color-mix(in_oklch,var(--primary)_15%,transparent),0_0_14px_color-mix(in_oklch,var(--primary)_55%,transparent)]'
            )}
          >
            {stage.done ? <Check className="size-3.5" strokeWidth={3} /> : index}
          </span>
        </Link>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={8}
        className="max-w-[220px] flex-col items-start gap-1 p-3"
      >
        <span className="text-xs font-semibold leading-tight">{label}</span>
        <span className="text-2xs leading-snug opacity-80">{stage.description}</span>
      </TooltipContent>
    </Tooltip>
  )
}
